import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { FileText, ChevronRight, ArrowRight, CheckCircle2 } from 'lucide-react'

// -------------------------------------------------------------------
// Route map: formType + returnType → URL path
// -------------------------------------------------------------------
const FORM_ROUTES = {
  '26Q': { Regular: '/tds-dashboard/26q', Correction: '/tds-dashboard/26q-correction' },
  '140': { Regular: '/tds-dashboard/140', Correction: '/tds-dashboard/140-correction' },
  '24Q': { Regular: '/tds-dashboard/24q', Correction: '/tds-dashboard/24q-correction' },
  '138': { Regular: '/tds-dashboard/138', Correction: '/tds-dashboard/138-correction' },
  '27Q': { Regular: '/tds-dashboard/27q', Correction: '/tds-dashboard/27q-correction' },
  '144': { Regular: '/tds-dashboard/144', Correction: '/tds-dashboard/144-correction' },
  '27EQ': { Regular: '/tds-dashboard/27eq', Correction: '/tds-dashboard/27eq-correction' },
  '143': { Regular: '/tds-dashboard/143', Correction: '/tds-dashboard/143-correction' },
}

// Financial year options (2023-24 to current)
const generateFyOptions = () => {
  const today = new Date()
  const currMonth = today.getMonth()
  const currYear = today.getFullYear()
  const currentFyStart = currMonth >= 3 ? currYear : currYear - 1
  const opts = []
  for (let start = currentFyStart; start >= 2023; start--) {
    opts.push(`${start}-${(start + 1).toString().slice(-2)}`)
  }
  return opts
}

// Quarter options filtered by FY vs current FY
const getQuarterOptions = (selectedFY) => {
  if (!selectedFY) return []
  const today = new Date()
  const currMonth = today.getMonth()
  const currYear = today.getFullYear()
  const currentFyStart = currMonth >= 3 ? currYear : currYear - 1
  const selectedFyStart = parseInt(selectedFY.split('-')[0])

  if (selectedFyStart > currentFyStart) return []
  if (selectedFyStart < currentFyStart) return ['Q1', 'Q2', 'Q3', 'Q4']

  // Same FY as current — show only elapsed quarters
  let maxQ = 4
  if (currMonth >= 3 && currMonth <= 5) maxQ = 1
  else if (currMonth >= 6 && currMonth <= 8) maxQ = 2
  else if (currMonth >= 9 && currMonth <= 11) maxQ = 3

  return ['Q1', 'Q2', 'Q3', 'Q4'].slice(0, maxQ)
}

// Form type options depend on selected FY
const getFormTypeOptions = (selectedFY) => {
  if (!selectedFY) {
    return [
      { value: '26Q', label: '26Q  – TDS on Non-Salary Payments to Residents' },
      { value: '24Q', label: '24Q  – TDS on Salary' },
      { value: '27Q', label: '27Q  – TDS on Payments to Non-Residents' },
      { value: '27EQ', label: '27EQ – TCS Collection at Source' },
    ]
  }
  const fyStart = parseInt(selectedFY.split('-')[0])
  const isNew = fyStart >= 2026
  return isNew
    ? [
      { value: '140', label: '140  – TDS on Non-Salary Payments to Residents (FY 2026+)' },
      { value: '138', label: '138  – TDS on Salary (FY 2026+)' },
      { value: '144', label: '144  – TDS on Payments to Non-Residents (FY 2026+)' },
      { value: '143', label: '143  – TCS Collection at Source (FY 2026+)' },
    ]
    : [
      { value: '26Q', label: '26Q  – TDS on Non-Salary Payments to Residents' },
      { value: '24Q', label: '24Q  – TDS on Salary' },
      { value: '27Q', label: '27Q  – TDS on Payments to Non-Residents' },
      { value: '27EQ', label: '27EQ – TCS Collection at Source' },
    ]
}

const RETURN_TYPES = [
  { value: 'Regular', label: 'Regular  – Original return for the quarter' },
  { value: 'Correction', label: 'Correction  – Revised / correction return' },
]

// -------------------------------------------------------------------
// Animated step label
// -------------------------------------------------------------------
const StepLabel = ({ num, label, done }) => (
  <div className="flex items-center gap-2 mb-2">
    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${done ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-500'}`}>
      {done ? <CheckCircle2 className="w-4 h-4" /> : num}
    </div>
    <span className={`text-sm font-semibold transition-colors duration-300 ${done ? 'text-emerald-600' : 'text-slate-600'}`}>{label}</span>
  </div>
)

// -------------------------------------------------------------------
// Main Component
// -------------------------------------------------------------------
const TdsReturnStart = () => {
  const navigate = useNavigate()
  const isInitialMount = useRef(true)

  const [selectedFY, setSelectedFY] = useState('')
  const [selectedForm, setSelectedForm] = useState('')
  const [selectedQuarter, setSelectedQuarter] = useState('')
  const [selectedReturnType, setSelectedReturnType] = useState('')

  const fyOptions = generateFyOptions()
  const formOptions = getFormTypeOptions(selectedFY)
  const quarterOptions = getQuarterOptions(selectedFY)

  // On mount: clear bad SELECTED_RETURN_TYPE ('tds-return'), and pre-load valid previous selections
  useEffect(() => {
    const savedFY = localStorage.getItem('tdsFinancialYear')
    const savedQtr = localStorage.getItem('tdsQuarter')
    const savedRT = localStorage.getItem('SELECTED_RETURN_TYPE')
    const savedForm = localStorage.getItem('tdsForm')

    if (savedFY && fyOptions.includes(savedFY)) setSelectedFY(savedFY)
    if (savedRT === 'Regular' || savedRT === 'Correction') setSelectedReturnType(savedRT)
    else localStorage.removeItem('SELECTED_RETURN_TYPE')  // clear invalid value

    // Form and quarter restored after FY is set (via useEffect below)
    if (savedForm) setSelectedForm(savedForm)
    if (savedQtr) setSelectedQuarter(savedQtr)
    // Mark initial mount done (so FY-change effect can reset on user interactions)
    isInitialMount.current = false
  }, [])

  // When FY changes by user interaction, reset form and quarter
  useEffect(() => {
    if (isInitialMount.current) return  // skip on first mount
    setSelectedForm('')
    setSelectedQuarter('')
  }, [selectedFY])

  // When FY changes and previously selected quarter is out of range, clear it
  useEffect(() => {
    if (selectedQuarter && !quarterOptions.includes(selectedQuarter)) {
      setSelectedQuarter('')
    }
  }, [selectedFY, quarterOptions, selectedQuarter])

  const allSelected = selectedFY && selectedForm && selectedQuarter && selectedReturnType

  const handleProceed = () => {
    if (!allSelected) return

    // Persist selections to localStorage
    localStorage.setItem('tdsFinancialYear', selectedFY)
    localStorage.setItem('tdsQuarter', selectedQuarter)
    localStorage.setItem('SELECTED_RETURN_TYPE', selectedReturnType)
    localStorage.setItem('tdsForm', selectedForm)

    // Navigate to the correct form route
    const routes = FORM_ROUTES[selectedForm]
    if (!routes) return
    const dest = routes[selectedReturnType] || routes['Regular']
    navigate(dest)
  }

  // Progress: count how many of the 4 fields are filled
  const filledCount = [selectedFY, selectedForm, selectedQuarter, selectedReturnType].filter(Boolean).length
  const progressPct = (filledCount / 4) * 100

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4">
      {/* Decorative blobs */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-blue-200/30 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-indigo-200/30 rounded-full blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className="relative w-full max-w-lg"
      >
        {/* Card */}
        <div className="bg-white/80 backdrop-blur-xl border border-white/60 rounded-2xl shadow-2xl overflow-hidden">

          {/* Header band */}
          <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-8 py-6 flex items-center gap-3">
            <div className="w-10 h-10 bg-white/15 rounded-xl flex items-center justify-center">
              <FileText className="w-6 h-6 text-white" strokeWidth={2} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white tracking-tight">New TDS Return</h1>
              <p className="text-slate-300 text-sm">Select details to begin</p>
            </div>
          </div>

          {/* Progress bar */}
          <div className="h-1 bg-slate-100">
            <motion.div
              className="h-full bg-gradient-to-r from-blue-500 to-emerald-500"
              initial={{ width: 0 }}
              animate={{ width: `${progressPct}%` }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
            />
          </div>

          {/* Form body */}
          <div className="px-8 py-7 space-y-6">

            {/* 1. Financial Year */}
            <div>
              <StepLabel num={1} label="Financial Year" done={!!selectedFY} />
              <select
                id="tds-start-fy"
                value={selectedFY}
                onChange={e => setSelectedFY(e.target.value)}
                className="w-full border border-slate-200 rounded-xl px-4 py-3 text-slate-800 text-sm font-medium bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all cursor-pointer"
              >
                <option value="">— Select Financial Year —</option>
                {fyOptions.map(fy => (
                  <option key={fy} value={fy}>{fy}</option>
                ))}
              </select>
            </div>

            {/* 2. Form Type */}
            <div>
              <StepLabel num={2} label="Form Type" done={!!selectedForm} />
              <select
                id="tds-start-form"
                value={selectedForm}
                onChange={e => setSelectedForm(e.target.value)}
                disabled={!selectedFY}
                className="w-full border border-slate-200 rounded-xl px-4 py-3 text-slate-800 text-sm font-medium bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">— Select Form Type —</option>
                {formOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* 3. Quarter */}
            <div>
              <StepLabel num={3} label="Quarter" done={!!selectedQuarter} />
              <div className="grid grid-cols-4 gap-2">
                {['Q1', 'Q2', 'Q3', 'Q4'].map(q => {
                  const available = quarterOptions.includes(q)
                  const selected = selectedQuarter === q
                  return (
                    <button
                      key={q}
                      id={`tds-start-quarter-${q}`}
                      type="button"
                      disabled={!selectedFY || !available}
                      onClick={() => setSelectedQuarter(q)}
                      className={`py-3 rounded-xl text-sm font-semibold border transition-all duration-200
                        ${selected
                          ? 'bg-slate-800 border-slate-800 text-white shadow-md'
                          : available
                            ? 'bg-white border-slate-200 text-slate-700 hover:border-slate-400 hover:bg-slate-50'
                            : 'bg-slate-50 border-slate-100 text-slate-300 cursor-not-allowed'
                        }`}
                    >
                      {q}
                    </button>
                  )
                })}
              </div>
              {selectedFY && quarterOptions.length === 0 && (
                <p className="text-xs text-amber-600 mt-1.5 ml-1">⚠ No quarters available for this financial year yet.</p>
              )}
            </div>

            {/* 4. Return Type */}
            <div>
              <StepLabel num={4} label="Return Type" done={!!selectedReturnType} />
              <div className="grid grid-cols-2 gap-3">
                {RETURN_TYPES.map(rt => {
                  const selected = selectedReturnType === rt.value
                  return (
                    <button
                      key={rt.value}
                      id={`tds-start-return-${rt.value.toLowerCase()}`}
                      type="button"
                      onClick={() => setSelectedReturnType(rt.value)}
                      className={`py-3 px-4 rounded-xl text-sm font-semibold border transition-all duration-200 text-left
                        ${selected
                          ? 'bg-slate-800 border-slate-800 text-white shadow-md'
                          : 'bg-white border-slate-200 text-slate-700 hover:border-slate-400 hover:bg-slate-50'
                        }`}
                    >
                      <div className="font-bold">{rt.value}</div>
                      <div className={`text-xs mt-0.5 ${selected ? 'text-slate-300' : 'text-slate-400'}`}>
                        {rt.value === 'Regular' ? 'Original return' : 'Revised / Correction'}
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Summary chip — shows after all 4 selected */}
            {allSelected && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 flex items-center gap-3"
              >
                <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                <div className="text-sm text-emerald-800">
                  <span className="font-semibold">Form {selectedForm}</span>
                  {' · '}{selectedFY}{' · '}{selectedQuarter}
                  {' · '}<span className="font-semibold">{selectedReturnType}</span>
                </div>
              </motion.div>
            )}

            {/* Proceed button */}
            <motion.button
              id="tds-start-proceed"
              type="button"
              onClick={handleProceed}
              disabled={!allSelected}
              whileTap={allSelected ? { scale: 0.97 } : {}}
              className={`w-full py-4 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all duration-300
                ${allSelected
                  ? 'bg-gradient-to-r from-slate-800 to-slate-700 text-white shadow-lg hover:from-slate-700 hover:to-slate-600 hover:shadow-xl cursor-pointer'
                  : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                }`}
            >
              {allSelected ? (
                <>Proceed to Return <ArrowRight className="w-4 h-4" /></>
              ) : (
                <>Select all 4 fields to continue <ChevronRight className="w-4 h-4" /></>
              )}
            </motion.button>
          </div>
        </div>

        {/* Footer note */}
        <p className="text-center text-xs text-slate-400 mt-4">
          You can change these selections later from the form header.
        </p>
      </motion.div>
    </div>
  )
}

export default TdsReturnStart
